import { type FC, useCallback, useEffect, useState } from 'react'
import Icon from '@/components/common/Icon'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useHistoryStore, type SaveStatus } from '@/shared/store/history.store'
import {
  autoSave,
  createSaveMutation,
  performSave,
} from '@/shared/store/use-cases/auto-save'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'

type Status = SaveStatus | 'offline'

const viewMapping: Record<Status, FC> = {
  offline: () => {
    const { t } = useTranslation()
    return (
      <>
        <Icon name="cloudOffOutlineRounded" />{' '}
        {t('ls_auto_save_trying_to_reconnect')}
      </>
    )
  },
  dirty: () => <Icon name="cloudAlertOutlineRounded" />,
  failed: () => {
    const { t } = useTranslation()
    return (
      <>
        <Icon name="cloudAlertOutlineRounded" /> {t('ls_auto_save_failed')}
      </>
    )
  },
  broken: () => {
    const { t } = useTranslation()
    return (
      <>
        <Icon name="cloudAlertOutlineRounded" /> {t('ls_auto_save_broken')}
      </>
    )
  },
  persisted: () => {
    const { t } = useTranslation()
    const [showSaved, setShowSaved] = useState(true)
    useEffect(() => {
      const timeout = setTimeout(() => {
        setShowSaved(false)
      }, 4_000)
      return () => clearTimeout(timeout)
    }, [])

    return (
      <>
        <Icon name="cloudDoneOutlineRounded" />
        <span
          className={[
            'inline-block overflow-hidden whitespace-nowrap transition-all duration-300',
            showSaved ? 'max-w-[300px] opacity-100' : 'max-w-0 opacity-0',
          ].join(' ')}
        >
          {t('ls_auto_save_saved')}
        </span>
      </>
    )
  },
  syncing: () => {
    const { t } = useTranslation()
    return (
      <>
        <span className="animate-spin">
          <Icon name="arrowsPath" />
        </span>{' '}
        {t('ls_auto_save_saving')}
      </>
    )
  },
}

const AutoSaveIndicator: FC = () => {
  const isConnected = useOnlineStatus()
  const { saveStatus } = useHistoryStore()
  const status = isConnected ? saveStatus : 'offline'
  const InnerView = viewMapping[status]

  // Create mutation with TanStack Query retry configuration
  const saveMutation = useMutation({
    mutationFn: performSave,
    // TanStack Query retry configuration
    retry: 3, // Retry up to 3 times on failure (matches MAX_RETRIES from before)
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s, max 30s
    onError: (error) => {
      // After all retries are exhausted, mark as broken
      useHistoryStore.setState({ saveStatus: 'broken' })
      console.error('Auto-save failed after all retries:', error)
    },
  })

  // Memoize the save function to avoid recreating the auto-save subscription on every render
  // mutateAsync from TanStack Query is stable, so this callback will only change if the mutation changes
  const saveFn = useCallback(() => {
    return saveMutation.mutateAsync()
  }, [saveMutation.mutateAsync])

  useEffect(() => {
    if (!isConnected) return // Only start auto-save when online

    // Create the save mutation wrapper and start auto-save
    const wrappedSaveFn = createSaveMutation(saveFn)
    const unsubscribe = autoSave(wrappedSaveFn, 10_000)

    return () => {
      unsubscribe()
    }
  }, [isConnected, saveFn])

  return (
    <div className="flex w-[300px] flex-row items-center justify-end gap-1.5 overflow-hidden text-sm font-normal text-zinc-300">
      <InnerView />
    </div>
  )
}

export default AutoSaveIndicator
